import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import type { Task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import UiButton from 'garaje/components/ui-button';
import type { ReportSource } from 'garaje/models/approval-status';
import type ConnectInvite from 'garaje/models/connect-invite';
import type ScreenerResult from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import type VisitApproval from 'garaje/models/visit-approval';
import { localCopy } from 'tracked-toolbox';

interface ReviewButtonsSignature {
  Args: {
    approveTask: Task<void, [ReportSource[]]>;
    denyTask?: Task<void, []>;
    disabled?: boolean;
    result: ScreenerResult;
    model: GuestWithVisitApproval | ConnectInvite;
    onApprove?: () => void;
    onDeny?: () => void;
    /** Phone-only: render the detail-page sticky-bar look (Deny left, Approve right, larger); sm: restores the default buttons. */
    stickyBar?: boolean;
  };
  Blocks: {
    deny: [];
  };
}

export default class ReviewButtons extends Component<ReviewButtonsSignature> {
  @localCopy('args.onApprove', () => {}) onApprove!: () => void;
  @localCopy('args.onDeny', () => {}) onDeny!: () => void;

  get visitApproval(): VisitApproval | undefined {
    if ('visitApproval' in this.args.model) {
      return this.args.model.visitApproval;
    }
    return undefined;
  }

  get disabled(): boolean {
    return Boolean(
      this.args.approveTask.isRunning ||
        this.args.denyTask?.isRunning ||
        this.visitApproval?.idScanningPendingResult ||
        this.args.disabled,
    );
  }

  get containerClass(): string {
    if (!this.args.denyTask) return 'flex flex-col';
    if (this.args.stickyBar) return 'flex flex-row gap-x-4 sm:gap-x-2';
    return 'flex flex-row gap-x-2';
  }

  get approveClass(): string {
    if (this.args.stickyBar) {
      return 'btn btn--small btn--outline min-w-0 flex-[2] !bg-red-50 !text-white !px-4 !py-[14px] sm:flex-initial sm:w-full sm:mt-2 sm:!bg-white sm:!text-carbon-70 sm:!py-2';
    }
    return 'btn btn--small btn--outline !bg-white w-full mt-2';
  }

  get denyClass(): string {
    if (this.args.stickyBar) {
      return 'btn btn--small btn--outline min-w-0 flex-1 order-first !bg-white !text-red-50 !px-4 !py-[14px] !ring-1 !ring-inset !ring-red-50/50 sm:order-none sm:flex-initial sm:w-full sm:mt-2 sm:!text-carbon-70 sm:!py-2 sm:!ring-carbon-20';
    }
    return 'btn btn--small btn--outline !bg-white w-full mt-2';
  }

  get iconClass(): string {
    const base = 'flex-none mr-2 align-middle';
    return this.args.stickyBar ? `hidden sm:inline ${base}` : base;
  }

  <template>
    <div class={{this.containerClass}}>
      <UiButton
        class={{this.approveClass}}
        data-test-approve-button={{@result.screenerSource}}
        disabled={{this.disabled}}
        {{on "click" (queue (perform @approveTask (array @result.screenerSource)) this.onApprove)}}
      >
        {{svgJar "checkmark-green" height="12" width="12" class=this.iconClass}}
        Approve
      </UiButton>
      {{#if @denyTask}}
        <UiButton
          class={{this.denyClass}}
          data-test-deny-button={{@result.screenerSource}}
          disabled={{this.disabled}}
          {{on "click" (queue (perform @denyTask) this.onDeny)}}
        >
          {{svgJar "cross-small-red" height="12" width="12" class=this.iconClass}}
          {{#if (has-block "deny")}}
            {{yield to="deny"}}
          {{else}}
            Deny
          {{/if}}
        </UiButton>
      {{/if}}
      {{#if this.visitApproval.idScanningPendingResult}}
        <EmberTooltip @side="top" @text="Please wait until the visitor's ID is done processing." />
      {{/if}}
    </div>
  </template>
}
